import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { CircularProgress } from '@mui/material';

import { DeviationQuestionUserResponseDto } from 'src/generated/services/TFinancialApi';
import { ChargeDomain, LegType } from 'src/use-cases/invoice/useQuestionnaire';
import { openToastError } from 'src/use-cases/toast/useToast';
import { DoubleChargeResolutionWrapper } from './components/DoubleChargeResolutionWrapper';
import { DoubleChargeDecisions } from './components/DoubleChargeResolutionTable';

import { useDeviationForm } from './hooks/useDeviationForm';
import { Questionnaire } from '../questionnaire/Questionnaire';
import { useShipmentSelector } from '../../ShipmentSelector';
import { DeviationActions } from './components/DeviationActions';
import { ProcurementDecisionProps } from './types';
import { DeviationFormResults } from './FormResults';
import { useSelectedDeviation } from './hooks/useSelectedDeviation';

export const DeviationDecision: FC<ProcurementDecisionProps> = (props) => {
  const { invoiceSummary, selectedDeviation } = props;
  const { shipment } = useShipmentSelector();
  const { setSelectedNextDeviation, currentDeviationIndex } = useSelectedDeviation();

  const currentDeviation = shipment.deviations.find((deviation) => deviation.id === selectedDeviation?.id);
  const additionalDefaultValues = currentDeviation
    ? {
        legType: currentDeviation?.chargeLegType,
        chargeDomain: currentDeviation?.chargeDomain,
      }
    : {};

  // Calculate deviation position for navigation
  const deviations = shipment?.deviations ?? [];
  const totalDeviations = deviations.length;

  const isDoubleCharge = selectedDeviation?.type === 'DOUBLE_CHARGE';

  const [doubleChargeDecisions, setDoubleChargeDecisions] = useState<DoubleChargeDecisions>({});
  const [showResolutionTable, setShowResolutionTable] = useState(false);

  const setupDoubleChargeInputs = useCallback(
    (noticeEl: ReactNode, _inputsEl: ReactNode, disabled = false) => (
      <>
        {noticeEl}
        {showResolutionTable && selectedDeviation?.id && (
          <DoubleChargeResolutionWrapper
            deviationId={selectedDeviation.id}
            decisions={doubleChargeDecisions}
            onDecisionChange={setDoubleChargeDecisions}
            disabled={disabled}
            showValidationError={!disabled}
          />
        )}
      </>
    ),
    [showResolutionTable, selectedDeviation?.id, doubleChargeDecisions]
  );

  const setupCustomInputs = isDoubleCharge ? setupDoubleChargeInputs : undefined;

  const handleDoubleChargeFormValues = useCallback(
    (formValues: Record<string, any>, questions: any[]) => {
      if (!isDoubleCharge || !questions) return;
      const doubleChargeQuestion = questions.find(
        (q: any) => q.questionDomainType === 'DO_YOU_ACCEPT_THIS_CHARGE_AS_IS' || q.questionDomainType === 'DO_YOU_ACCEPT_THIS_DOUBLE_CHARGE'
      );
      if (!doubleChargeQuestion) return;
      const selectedOptionId = formValues[doubleChargeQuestion.id];
      if (!selectedOptionId) {
        setShowResolutionTable(false);
        return;
      }
      const selectedOption = doubleChargeQuestion.answerOptions?.find((opt: any) => opt.id === selectedOptionId);
      const isResolveAll = selectedOption?.type === 'RESOLVE_ALL';
      setShowResolutionTable(!!isResolveAll);
    },
    [isDoubleCharge]
  );

  const {
    procurementQuestions,
    procurementResponse,
    logisticQuestions,
    logisticResponse,
    supplyChainQuestions,
    supplyChainResponse,
    financeQuestions,
    financeResponse,
    opsAdminQuestions,
    opsAdminResponse,
    isLogisticEditable,
    isProcurementEditable,
    isSupplyChainEditable,
    isFinanceEditable,
    isOpsAdminEditable,
    isProcurementReadOnly,
    setIsProcurementReadOnly,
    isLogisticReadOnly,
    setIsLogisticReadOnly,
    isSupplyChainReadOnly,
    setIsSupplyChainReadOnly,
    isFinanceReadOnly,
    setIsFinanceReadOnly,
    isOpsAdminReadOnly,
    setIsOpsAdminReadOnly,
    isLoading,
    handleSubmit,
    // Workflow path guards
    isStandardWorkflow,
    isThresholdWorkflow,
    isOpsAdminWorkflow,
  } = useDeviationForm(invoiceSummary, selectedDeviation);

  // Detect RESOLVE_ALL from saved responses so table shows in preview/read-only mode
  useEffect(() => {
    if (!isDoubleCharge) return;
    const responses = [procurementResponse, logisticResponse, supplyChainResponse, financeResponse, opsAdminResponse];
    const hasResolveAll = responses.some(
      (responseList) => responseList?.some((r: any) => r.questionDomainType === 'DO_YOU_ACCEPT_THIS_CHARGE_AS_IS' && r.answerType === 'RESOLVE_ALL')
    );
    if (hasResolveAll) setShowResolutionTable(true);
  }, [isDoubleCharge, procurementResponse, logisticResponse, supplyChainResponse, financeResponse, opsAdminResponse]);

  // Rate card functionality
  const onDownloadConfiguredRateCard = async () => {
    if (shipment?.shipmentInvoiceId) {
      window.open(`/configured-rate-card/${shipment.shipmentInvoiceId}`, '_blank');
    } else {
      openToastError(new Error('Shipment invoice ID not found'));
    }
  };

  const handleSubmitQuestionnaire = async (data: DeviationQuestionUserResponseDto[], formData?: any) => {
    // Extract leg assignment and charge domain from form data and pass directly to handleSubmit
    const newChargeAnswer =
      formData?.legType || formData?.chargeDomain
        ? {
            legType: formData.legType as LegType,
            chargeDomain: formData.chargeDomain as ChargeDomain,
          }
        : undefined;

    if (isDoubleCharge && showResolutionTable) {
      const doubleChargeLineDecisions = Object.entries(doubleChargeDecisions).map(([deviationId, accepted]) => ({
        deviationId,
        accepted: accepted as boolean,
      }));
      await handleSubmit(data, newChargeAnswer, doubleChargeLineDecisions);
      return;
    }

    await handleSubmit(data, newChargeAnswer);
  };

  // Disable submit when double charge resolution table is shown but has unfilled decisions
  const hasUnfilledDecisions = isDoubleCharge && showResolutionTable && Object.values(doubleChargeDecisions).some((v) => v === undefined);

  // Check if workflow is complete (approved/rejected)
  // When currentDeviationWorkflow is null and nextWorkflowAction is null, show all responses without edit
  const isWorkflowComplete = !shipment?.currentDeviationWorkflow && !shipment?.nextWorkflowAction;

  return (
    <div className='flex flex-col h-full'>
      {isLoading && (
        <div className='flex justify-center items-center py-12'>
          <div className='flex flex-col items-center gap-3'>
            <CircularProgress size={32} color='primary' />
          </div>
        </div>
      )}
      <div className='flex-1 flex flex-col gap-48'>
        {/* Workflow Path 1: Standard Review - Operation & Procurement */}
        {/* Show Procurement results when workflow complete OR in standard workflow */}
        {(isWorkflowComplete || isStandardWorkflow) && procurementQuestions?.length > 0 && !isLoading && (
          <>
            {!isWorkflowComplete && !isProcurementReadOnly && isProcurementEditable && (
              <Questionnaire
                title='Procurement Decision'
                questions={procurementQuestions}
                answers={procurementResponse}
                onSubmit={handleSubmitQuestionnaire}
                key={`questionnaire-procurement-${procurementResponse?.length}`}
                isEditable={isProcurementEditable}
                isReadOnly={isProcurementReadOnly}
                isDisableSubmit={hasUnfilledDecisions}
                theme='custom'
                onClickEdit={() => setIsProcurementReadOnly(false)}
                onCancel={() => setIsProcurementReadOnly(true)}
                additionalDefaultValues={additionalDefaultValues}
                additionalValidationSchema={Yup.object({
                  legType: Yup.string().required('Leg type is required'),
                  chargeDomain: Yup.string().required('Charge domain is required'),
                })}
                moreActions={<DeviationActions invoiceSummary={invoiceSummary} onDownloadConfiguredRateCard={onDownloadConfiguredRateCard} />}
                deviationId={selectedDeviation?.id}
                currentDeviationIndex={currentDeviationIndex}
                totalDeviations={totalDeviations}
                onNavigateNext={setSelectedNextDeviation}
                setupCustomInputs={setupCustomInputs}
                onFormValuesChange={(fv) => handleDoubleChargeFormValues(fv, procurementQuestions)}
              />
            )}
            {(isProcurementReadOnly || !isProcurementEditable || isWorkflowComplete) && procurementResponse?.length > 0 && (
              <DeviationFormResults
                title='Procurement Decision'
                theme='custom'
                answers={procurementResponse}
                legType={currentDeviation?.chargeLegType}
                chargeDomain={currentDeviation?.chargeDomain}
                isEditable={isWorkflowComplete ? false : isProcurementEditable}
                onClickEdit={() => setIsProcurementReadOnly(false)}
                moreActions={<DeviationActions invoiceSummary={invoiceSummary} onDownloadConfiguredRateCard={onDownloadConfiguredRateCard} />}
                deviationId={selectedDeviation?.id}
              />
            )}
          </>
        )}
        {/* Show Logistics results when workflow complete OR in standard workflow */}
        {(isWorkflowComplete || isStandardWorkflow) && logisticQuestions?.length > 0 && !isLoading && (
          <>
            {!isWorkflowComplete && !isLogisticReadOnly && isLogisticEditable && (
              <Questionnaire
                title='Logistics Decision'
                questions={logisticQuestions}
                answers={logisticResponse}
                onSubmit={handleSubmitQuestionnaire}
                key={`questionnaire-logistic-${logisticResponse?.length}`}
                isEditable={isLogisticEditable}
                isReadOnly={isLogisticReadOnly}
                isDisableSubmit={hasUnfilledDecisions}
                theme='custom'
                onClickEdit={() => setIsLogisticReadOnly(false)}
                onCancel={() => setIsLogisticReadOnly(true)}
                deviationId={selectedDeviation?.id}
                moreActions={<DeviationActions invoiceSummary={invoiceSummary} onDownloadConfiguredRateCard={onDownloadConfiguredRateCard} />}
                currentDeviationIndex={currentDeviationIndex}
                totalDeviations={totalDeviations}
                onNavigateNext={setSelectedNextDeviation}
                setupCustomInputs={setupCustomInputs}
                onFormValuesChange={(fv) => handleDoubleChargeFormValues(fv, logisticQuestions)}
              />
            )}
            {(isLogisticReadOnly || !isLogisticEditable || isWorkflowComplete) && logisticResponse?.length > 0 && (
              <DeviationFormResults
                title='Logistics Decision'
                theme='custom'
                onClickEdit={() => setIsLogisticReadOnly(false)}
                answers={logisticResponse}
                legType={currentDeviation?.chargeLegType}
                chargeDomain={currentDeviation?.chargeDomain}
                isEditable={isWorkflowComplete ? false : isLogisticEditable}
                moreActions={<DeviationActions invoiceSummary={invoiceSummary} onDownloadConfiguredRateCard={onDownloadConfiguredRateCard} />}
                deviationId={selectedDeviation?.id}
              />
            )}
          </>
        )}

        {/* Workflow Path 2: Threshold Review - Supply Chain & Finance */}
        {/* Show Supply Chain results when workflow complete OR in threshold workflow */}
        {(isWorkflowComplete || isThresholdWorkflow) && supplyChainQuestions?.length > 0 && !isLoading && (
          <>
            {!isWorkflowComplete && !isSupplyChainReadOnly && isSupplyChainEditable && (
              <Questionnaire
                title='Supply Chain Decision'
                questions={supplyChainQuestions}
                answers={supplyChainResponse}
                onSubmit={handleSubmitQuestionnaire}
                key={`questionnaire-supply-chain-${supplyChainResponse?.length}`}
                isEditable={isSupplyChainEditable}
                isReadOnly={isSupplyChainReadOnly}
                isDisableSubmit={hasUnfilledDecisions}
                theme='custom'
                onClickEdit={() => setIsSupplyChainReadOnly(false)}
                onCancel={() => setIsSupplyChainReadOnly(true)}
                additionalDefaultValues={additionalDefaultValues}
                additionalValidationSchema={Yup.object({
                  legType: Yup.string().required('Leg type is required'),
                  chargeDomain: Yup.string().required('Charge domain is required'),
                })}
                deviationId={selectedDeviation?.id}
                moreActions={<DeviationActions invoiceSummary={invoiceSummary} onDownloadConfiguredRateCard={onDownloadConfiguredRateCard} />}
                currentDeviationIndex={currentDeviationIndex}
                totalDeviations={totalDeviations}
                onNavigateNext={setSelectedNextDeviation}
                setupCustomInputs={setupCustomInputs}
                onFormValuesChange={(fv) => handleDoubleChargeFormValues(fv, supplyChainQuestions)}
              />
            )}
            {(isSupplyChainReadOnly || !isSupplyChainEditable || isWorkflowComplete) && supplyChainResponse?.length > 0 && (
              <DeviationFormResults
                title='Supply Chain Decision'
                theme='custom'
                onClickEdit={() => setIsSupplyChainReadOnly(false)}
                answers={supplyChainResponse}
                legType={currentDeviation?.chargeLegType}
                chargeDomain={currentDeviation?.chargeDomain}
                isEditable={isWorkflowComplete ? false : isSupplyChainEditable}
                moreActions={<DeviationActions invoiceSummary={invoiceSummary} onDownloadConfiguredRateCard={onDownloadConfiguredRateCard} />}
                deviationId={selectedDeviation?.id}
              />
            )}
          </>
        )}

        {/* Show Finance results when workflow complete OR in threshold workflow */}
        {(isWorkflowComplete || isThresholdWorkflow) && financeQuestions?.length > 0 && !isLoading && (
          <>
            {!isWorkflowComplete && !isFinanceReadOnly && isFinanceEditable && (
              <div className='border-t border-neutral-20 pt-24'>
                <Questionnaire
                  title='Finance Decision'
                  questions={financeQuestions}
                  answers={financeResponse}
                  onSubmit={handleSubmitQuestionnaire}
                  key={`questionnaire-finance-${financeResponse?.length}`}
                  isEditable={isFinanceEditable}
                  isReadOnly={isFinanceReadOnly}
                  isDisableSubmit={hasUnfilledDecisions}
                  theme='custom'
                  onClickEdit={() => setIsFinanceReadOnly(false)}
                  onCancel={() => setIsFinanceReadOnly(true)}
                  additionalDefaultValues={additionalDefaultValues}
                  additionalValidationSchema={Yup.object({
                    legType: Yup.string().required('Leg type is required'),
                    chargeDomain: Yup.string().required('Charge domain is required'),
                  })}
                  deviationId={selectedDeviation?.id}
                  currentDeviationIndex={currentDeviationIndex}
                  totalDeviations={totalDeviations}
                  onNavigateNext={setSelectedNextDeviation}
                  setupCustomInputs={setupCustomInputs}
                  onFormValuesChange={(fv) => handleDoubleChargeFormValues(fv, financeQuestions)}
                />
              </div>
            )}
            {(isFinanceReadOnly || !isFinanceEditable || isWorkflowComplete) && financeResponse?.length > 0 && (
              <div className='border-t border-neutral-20 pt-24'>
                <DeviationFormResults
                  title='Finance Decision'
                  theme='custom'
                  onClickEdit={() => setIsFinanceReadOnly(false)}
                  answers={financeResponse}
                  legType={currentDeviation?.chargeLegType}
                  chargeDomain={currentDeviation?.chargeDomain}
                  isEditable={isWorkflowComplete ? false : isFinanceEditable}
                  deviationId={selectedDeviation?.id}
                />
              </div>
            )}
          </>
        )}

        {/* Workflow Path 3: Ops Admin Review */}
        {(isWorkflowComplete || isOpsAdminWorkflow) && opsAdminQuestions?.length > 0 && !isLoading && (
          <>
            {!isWorkflowComplete && !isOpsAdminReadOnly && isOpsAdminEditable && (
              <Questionnaire
                title='Ops Admin Decision'
                questions={opsAdminQuestions}
                answers={opsAdminResponse}
                onSubmit={handleSubmitQuestionnaire}
                key={`questionnaire-ops-admin-${opsAdminResponse?.length}`}
                isEditable={isOpsAdminEditable}
                isReadOnly={isOpsAdminReadOnly}
                isDisableSubmit={hasUnfilledDecisions}
                theme='custom'
                onClickEdit={() => setIsOpsAdminReadOnly(false)}
                onCancel={() => setIsOpsAdminReadOnly(true)}
                additionalDefaultValues={additionalDefaultValues}
                additionalValidationSchema={Yup.object({
                  legType: Yup.string().required('Leg type is required'),
                  chargeDomain: Yup.string().required('Charge domain is required'),
                })}
                deviationId={selectedDeviation?.id}
                moreActions={<DeviationActions invoiceSummary={invoiceSummary} onDownloadConfiguredRateCard={onDownloadConfiguredRateCard} />}
                currentDeviationIndex={currentDeviationIndex}
                totalDeviations={totalDeviations}
                onNavigateNext={setSelectedNextDeviation}
                setupCustomInputs={setupCustomInputs}
                onFormValuesChange={(fv) => handleDoubleChargeFormValues(fv, opsAdminQuestions)}
              />
            )}
            {(isOpsAdminReadOnly || !isOpsAdminEditable || isWorkflowComplete) && opsAdminResponse?.length > 0 && (
              <DeviationFormResults
                title='Ops Admin Decision'
                theme='custom'
                onClickEdit={() => setIsOpsAdminReadOnly(false)}
                answers={opsAdminResponse}
                legType={currentDeviation?.chargeLegType}
                chargeDomain={currentDeviation?.chargeDomain}
                isEditable={isWorkflowComplete ? false : isOpsAdminEditable}
                moreActions={<DeviationActions invoiceSummary={invoiceSummary} onDownloadConfiguredRateCard={onDownloadConfiguredRateCard} />}
                deviationId={selectedDeviation?.id}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
